import { useHistory, useLocation } from 'react-router-dom';
import * as React from 'react';

import { ClickTracker } from 'bernie-analytics';

import { ListingFooterViewModel } from 'models';

import { AnalyticsConfig } from 'uitk-react-utils';
import { UitkCardContentSection } from 'uitk-react-cards';
import { UitkIcon } from 'uitk-react-icons';
import { UitkLayoutFlex } from 'uitk-react-layout-flex';
import { UitkLayoutPosition } from 'uitk-react-layout-position';
import { UitkLink } from 'uitk-react-link';

import { ActionDetailsType } from '__generated__/api';
import { DoubleLink } from 'components/common/media/double-link';
import { MessagingCardActions } from 'components/common/messaging-card/messaging-card-actions';
import { TargetableLink } from 'components/common/targetable-link';
import { UserSetListingComponentProps } from 'components/search/listing';

const DoubleLinkTracked = ClickTracker(DoubleLink);

export interface FooterProps extends UserSetListingComponentProps {
  listingClickHandler?: () => void;
  listingFooter?: ListingFooterViewModel;
}

export const ListingFooter: React.FunctionComponent<FooterProps> = ({
  analytics,
  listingFooter,
  model,
  dataAttributes,
  listingClickHandler,
  referralObject,
  uiPage,
}) => {
  const history = useHistory();
  const location = useLocation();

  if (!model || !listingFooter) {
    return null;
  }

  const { hasAvailableOffer, propertyDetailsLink, name, travelAd } = model;
  const { actionModel, icon } = listingFooter;

  if ((!hasAvailableOffer && actionModel?.actionDetails?.name !== ActionDetailsType.DATEPICKER) || !actionModel?.text) {
    return null;
  }

  const { href, rfrr, text, actionDetails } = actionModel;

  const listingDataAttributes = dataAttributes ? dataAttributes.listing : {};
  const analyticHotelOpenProps = referralObject ? referralObject.navigation : {};

  const hotelLinkProps = {
    name,
    rfrr,
    'data-stid': 'listing-footer-open-infosite',
    linkName: 'View selected hotel',
    to: propertyDetailsLink?.uri?.relativePath,
    onClick: listingClickHandler,
    ...analyticHotelOpenProps,
    ...listingDataAttributes,
  };

  const signInLinkProps = {
    'data-stid': 'listing-footer-sign-in',
    linkName: 'Member only deal sign in',
    rfrr,
    to: href,
    trackImmediate: true,
  };

  const brandedDealLinkProps = {
    ...signInLinkProps,
    'data-stid': 'listing-footer-branded-deal',
    children: text,
  };

  let doubleLinkProps;
  let buttonLinkProps;
  let shouldLinkTarget = false;

  const actionType = actionDetails && actionDetails.name;

  switch (actionType) {
    case 'OPEN':
      doubleLinkProps = hotelLinkProps;

      if (!(travelAd && travelAd.clickRedirectLinkoffTrackingUrl)) {
        shouldLinkTarget = true;
      }

      break;
    case 'AUTHWINDOW':
      doubleLinkProps = {
        ...signInLinkProps,
        onClick: MessagingCardActions.resolveAction(history, location, actionModel, analytics),
      };

      break;
    case 'DATEPICKER':
      buttonLinkProps = {
        onClick: () => uiPage.dispatchOpenNewDatePickerEvent(),
        analytics: analytics.setupTracker(rfrr, text),
        linkName: text,
        href,
      };

      break;
    case 'EXTERNAL_LINK':
      doubleLinkProps = brandedDealLinkProps;

      break;
    default:
      return null;
  }

  const footerLink = buttonLinkProps ? (
    <ButtonLink {...buttonLinkProps} />
  ) : (
    <UitkLayoutFlex justifyContent="end">
      <TextIconDoubleLink icon={icon} text={text} shouldLinkTarget={shouldLinkTarget} doubleLinkProps={doubleLinkProps} />
    </UitkLayoutFlex>
  );

  return (
    <UitkCardContentSection tag="footer" border="top">
      <UitkLayoutPosition type={'relative'} position={{ zindex: 'layer2' }}>
        {footerLink}
      </UitkLayoutPosition>
    </UitkCardContentSection>
  );
};

interface TextIconDoubleLinkModel {
  icon?: string;
  text?: string;
  shouldLinkTarget?: boolean;
  doubleLinkProps?: any;
}
const TextIconDoubleLink = ({ text, icon, shouldLinkTarget, doubleLinkProps }: TextIconDoubleLinkModel) => {
  const trackedLink = (
    <UitkLink>
      <DoubleLinkTracked {...doubleLinkProps}>
        {text}
        {icon && <UitkIcon name={icon} />}
      </DoubleLinkTracked>
    </UitkLink>
  );

  return <span>{shouldLinkTarget ? <TargetableLink>{trackedLink}</TargetableLink> : trackedLink}</span>;
};

interface ButtonLinkProps {
  onClick: () => void;
  linkName: string;
  href: string;
  analytics?: AnalyticsConfig;
}
const ButtonLink = ({ onClick, analytics, linkName, href }: ButtonLinkProps) => {
  const [isMounted, setIsMounted] = React.useState(false);
  React.useEffect(() => {
    setIsMounted(true);
  }, []);

  return (
    <UitkLink onClick={onClick} analytics={analytics}>
      {isMounted ? <button>{linkName}</button> : <a href={href}>{linkName}</a>}
    </UitkLink>
  );
};
ButtonLink.displayName = 'ButtonLink';
